import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = {
  key: 1,
  class: "text-xs border bg-white p-4 rounded flex flex-col gap-4 shadow-lg max-w-[500px]"
}
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = { class: "flex flex-col gap-1" }
const _hoisted_5 = {
  key: 0,
  class: "flex items-center gap-1"
}
const _hoisted_6 = { class: "rounded-lg px-2 py-0.5 text-white plannedBg truncate capitalize text-center min-w-[40px]" }
const _hoisted_7 = {
  key: 1,
  class: "flex items-center gap-1"
}
const _hoisted_8 = { class: "rounded-lg px-2 py-0.5 text-white bg-green truncate capitalize text-center min-w-[40px]" }
const _hoisted_9 = {
  key: 1,
  class: "flex items-center w-[80px] justify-end"
}
const _hoisted_10 = {
  key: 0,
  class: "text-xs whitespace-pre-line"
}

import { computed } from "vue";
import { useI18n } from "vue-i18n";
import OaiTooltip from "shared/components/other/OaiTooltip.vue";
import { HierarchyTagStore } from "shared/types/HierarchyTag";
import { PlannerItemWithChildren } from "shared/types/Plan";
import projectProgressService from "@/services/projectProgressService";
import { PlannerItemProgress } from "@/types/Plan";
import ProjectProgressItemName from "@/views/planner/components/ProjectProgressItemName.vue";

const precision = 1;


export default /*#__PURE__*/_defineComponent({
  __name: 'ProjectProgressItemDetail',
  props: {
    plannerItem: {},
    plannerItemProgress: {},
    simplified: { type: Boolean },
    actualEventDates: {},
    plannedEventDates: {},
    totalProgress: {},
    tags: {}
  },
  setup(__props: any) {

const props = __props;

const { t, locale } = useI18n();

const formatNumber = (number: number) =>
  number.toLocaleString(locale.value, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: false,
  });

const formatPercentage = (percentage: number | null) =>
  percentage !== null ? `${formatNumber(percentage * 100)}%` : "-";

const percentage = computed(() => {
  if (!props.plannerItemProgress) {
    return null;
  }
  return projectProgressService.calculatePercentage(
    props.plannerItemProgress.finished_working_days,
    props.plannerItemProgress.total_working_days,
  );
});

const getProgressTooltip = (
  plannerItem: PlannerItemWithChildren,
  plannerItemProgress: PlannerItemProgress,
  options?: { detailOnly?: boolean },
) => {
  const finishedWorkingDays = formatNumber(
    plannerItem.children && plannerItem.children.length === 0
      ? plannerItemProgress.actual_finished_working_days
      : plannerItemProgress.finished_working_days,
  );

  const status =
    percentage.value && percentage.value >= 1
      ? "completed"
      : percentage.value && percentage.value <= 0
      ? "not_started"
      : "active";

  const exceedsTotalWorkDays =
    plannerItem.children &&
    plannerItem.children.length === 0 &&
    plannerItemProgress.actual_finished_working_days > plannerItemProgress.total_working_days;

  const notFinishedLabel = exceedsTotalWorkDays
    ? `95% ${t("analytics.reports.query_value_progress.fixe_value")}`
    : `${formatPercentage(percentage.value)}`;

  const isParent = plannerItem.children && plannerItem.children.length > 0;
  const parentTooltip = isParent
    ? ` (${t("analytics.reports.query_value_progress.finished_working_hours_parent_tooltip")})`
    : "";

  const detail =
    status === "completed" || status === "not_started" || isParent
      ? `${formatNumber(plannerItemProgress.total_working_days)} ${t(
          "analytics.reports.query_value_progress.planned_working_days",
        )}`
      : `${t("analytics.reports.query_value_progress.worked_out_of", {
          actual: finishedWorkingDays,
          total: formatNumber(plannerItemProgress.total_working_days),
        })} <b>(${notFinishedLabel})</b>`;

  const aggregationDetail =
    isParent && status === "active"
      ? `\n• ${t(
          "analytics.reports.query_value_progress.aggregated_progress",
        )}: <b>${notFinishedLabel}</b>`
      : "";

  return `${
    !options?.detailOnly
      ? `<b>${t(
          `analytics.reports.query_value_progress.status.${status}`,
        )}</b>${parentTooltip}\n\n• `
      : ""
  }${detail}${aggregationDetail}`;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!_ctx.simplified || (_ctx.plannerItem.children && _ctx.plannerItem.children.length === 0))
      ? (_openBlock(), _createBlock(OaiTooltip, {
          key: 0,
          cls: "shrink-0 w-[80px] flex items-center cursor-pointer",
          simple: !_ctx.simplified
        }, {
          tooltip: _withCtx(() => [
            (_ctx.plannerItemProgress && !_ctx.simplified)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "text-xs whitespace-pre-line",
                  innerHTML: getProgressTooltip(_ctx.plannerItem, _ctx.plannerItemProgress)
                }, null, 8, _hoisted_1))
              : _createCommentVNode("", true),
            (_ctx.plannerItemProgress && _ctx.simplified)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createVNode(ProjectProgressItemName, {
                    plannerItem: _ctx.plannerItem,
                    tags: _ctx.tags,
                    simplified: true
                  }, null, 8, ["plannerItem", "tags"]),
                  _createElementVNode("div", {
                    class: "whitespace-pre-line",
                    innerHTML: getProgressTooltip(_ctx.plannerItem, _ctx.plannerItemProgress, { detailOnly: true })
                  }, null, 8, _hoisted_3),
                  _createElementVNode("div", _hoisted_4, [
                    (_ctx.plannedEventDates)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                          _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(t)("analytics.planner.planned_event_name")), 1),
                          _createTextVNode(" " + _toDisplayString(_ctx.plannedEventDates), 1)
                        ]))
                      : _createCommentVNode("", true),
                    (_ctx.actualEventDates)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                          _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(t)("analytics.planner.actual_event_name")), 1),
                          _createTextVNode(" " + _toDisplayString(_ctx.actualEventDates), 1)
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          default: _withCtx(() => [
            _createElementVNode("div", {
              class: _normalizeClass(["w-full h-full flex items-center justify-center rounded text-white", {
        'bg-green-200': percentage.value !== null && percentage.value === 1,
        'bg-gray-300': percentage.value !== null && percentage.value === 0,
      }]),
              style: _normalizeStyle({
        background:
          percentage.value !== null && percentage.value > 0 && percentage.value < 1
            ? `linear-gradient(90deg, #e7bc66 ${percentage.value * 100}%, #d1d5db ${percentage.value * 100}%)`
            : undefined,
      })
            }, _toDisplayString(formatPercentage(percentage.value)), 7)
          ]),
          _: 1
        }, 8, ["simple"]))
      : _createCommentVNode("", true),
    (!_ctx.simplified)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createVNode(OaiTooltip, {
            cls: "shrink-0 cursor-pointer pl-1",
            position: "left"
          }, {
            tooltip: _withCtx(() => [
              (_ctx.plannerItemProgress && _ctx.totalProgress)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _createElementVNode("div", null, [
                      _createElementVNode("b", null, _toDisplayString(_unref(t)("analytics.reports.query_value_progress.weight")), 1)
                    ]),
                    _createTextVNode(" " + _toDisplayString(`\n•${formatNumber(_ctx.plannerItemProgress.total_working_days)} / ${formatNumber(
              _ctx.totalProgress.totalWorkingDays,
            )} (${_unref(t)("analytics.reports.query_value_progress.planner_item")}/${_unref(t)(
              "analytics.reports.query_value_progress.project",
            )}) ${_unref(t)(
              "analytics.reports.query_value_progress.planned_working_days",
            )} = ${formatPercentage(_ctx.plannerItemProgress?.weight ?? null)}`), 1)
                  ]))
                : _createCommentVNode("", true)
            ]),
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(formatPercentage(_ctx.plannerItemProgress?.weight ?? null)) + " ", 1)
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})